import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff, AlertTriangle, Lock, User, Server, Activity, ChevronRight, Cpu } from 'lucide-react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loginStatus, setLoginStatus] = useState<'idle' | 'loading' | 'error' | 'success'>('idle');
  const [validationErrors, setValidationErrors] = useState({ email: '', password: '' });
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [systemStatus, setSystemStatus] = useState({
    securityModules: Math.floor(Math.random() * 10) + 90, // 90-99%
    databaseConnection: Math.floor(Math.random() * 5) + 95, // 95-99%
    apiServers: Math.floor(Math.random() * 5) + 95, // 95-99%
    lastScan: new Date().toLocaleTimeString()
  });
  
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated, error: authError } = useAuth();
  
  // If user is already authenticated, redirect to dashboard
  useEffect(() => {
    if (isAuthenticated) {
      // Fix: Use a properly formed path instead of potentially trying to construct a URL
      const from = location.state?.from?.pathname || "/";
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);
  
  // Simulate periodic system status updates
  useEffect(() => {
    const interval = setInterval(() => {
      setSystemStatus(prev => ({
        securityModules: Math.min(99, Math.max(90, prev.securityModules + (Math.random() > 0.5 ? 1 : -1))),
        databaseConnection: Math.min(99, Math.max(95, prev.databaseConnection + (Math.random() > 0.5 ? 0.5 : -0.5))),
        apiServers: Math.min(99, Math.max(95, prev.apiServers + (Math.random() > 0.5 ? 0.5 : -0.5))),
        lastScan: new Date().toLocaleTimeString()
      }));
    }, 3000);
    
    return () => clearInterval(interval);
  }, []);
  
  // Set default email from environment variable if available
  useEffect(() => {
    const defaultEmail = import.meta.env.VITE_ADMIN_EMAIL;
    if (defaultEmail) {
      setEmail(defaultEmail);
    }
  }, []);
  
  const validateForm = () => {
    let isValid = true;
    const errors = { email: '', password: '' };
    
    // Email validation
    if (!email) {
      errors.email = 'Email is required';
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      errors.email = 'Email format is invalid';
      isValid = false;
    }
    
    // Password validation
    if (!password) {
      errors.password = 'Password is required';
      isValid = false;
    }
    
    setValidationErrors(errors);
    return isValid;
  };
  
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoginStatus('loading');
    setLoginAttempts(prev => prev + 1);
    
    try {
      const success = await login(email, password);
      if (success) {
        setLoginStatus('success');
        // Redirect handled by the useEffect above
      } else {
        setLoginStatus('error');
      }
    } catch (err) {
      console.error('Login error:', err);
      setLoginStatus('error');
    }
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-slate-950 to-black text-white relative overflow-x-hidden">
      {/* Background Video */}
      <div className="fixed inset-0 z-0">
        <video
          autoPlay
          loop
          muted
          playsInline
          className="w-full h-full object-cover filter blur-sm opacity-30"
        >
          <source src="https://pub-47f890e60ca1453698471756a9133329.r2.dev/5645050_Coll_wavebreak_Animation_1920x1080.mp4" type="video/mp4" />
        </video>
        {/* Overlay to darken the video */}
        <div className="absolute inset-0 bg-black/60"></div>
      </div>

      {/* Dynamic Cyber Threat Map Background */}
      <div className="absolute inset-0 z-10 opacity-40">
        <div className="w-full h-full bg-slate-950"></div>
      </div>
      
      {/* Glowing Red Accent */}
      <div className="fixed -top-20 -right-20 w-96 h-96 bg-red-900/20 rounded-full blur-[100px] z-20"></div>
      <div className="fixed top-1/3 -left-20 w-80 h-80 bg-red-900/10 rounded-full blur-[100px] z-20"></div>
      
      {/* Grid Background */}
      <div className="fixed inset-0 z-30 opacity-10 animated-grid"></div>

      {/* Animated Security Scan Line */}
      <div className="fixed inset-0 z-40">
        <div className="h-px w-full bg-gradient-to-r from-transparent via-red-600 to-transparent absolute"
          style={{ 
            animation: 'scanVertical 8s infinite linear',
            boxShadow: '0 0 15px rgba(220, 38, 38, 0.5), 0 0 30px rgba(220, 38, 38, 0.3)'
          }}>
        </div>
        <div className="w-px h-full bg-gradient-to-b from-transparent via-red-600 to-transparent absolute"
          style={{ 
            animation: 'scanHorizontal 10s infinite linear',
            boxShadow: '0 0 15px rgba(220, 38, 38, 0.5), 0 0 30px rgba(220, 38, 38, 0.3)'
          }}>
        </div>
      </div>
      
      {/* Login Container */}
      <div className="min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8 relative z-50">
        <div className="w-full max-w-md">
          {/* Logo and Header */}
          <div className="text-center mb-6 sm:mb-8">
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center justify-center h-16 w-16 sm:h-20 sm:w-20 rounded-full bg-red-900/20 border border-red-700/30 mb-4 relative"
            >
              <img src="https://pub-0f77feecf89849e2bd80779e26ad94bb.r2.dev/shieldxgif.gif" alt="ShieldX" className="h-10 w-10 sm:h-12 sm:w-12" />
              {/* Animated ping effect */}
              <span className="absolute h-full w-full rounded-full border-2 border-red-600/30 animate-ping"></span>
            </motion.div>
            
            <motion.h2
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="text-2xl sm:text-3xl font-bold tracking-tight"
            >
              AlertStrike <span className="text-red-600">Command Center</span>
            </motion.h2>
            
            <motion.p
              initial={{ y: -10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.5 }}
              className="mt-2 text-xs sm:text-sm text-slate-400"
            >
              Advanced AI SIEM & EDR Threat Monitoring
            </motion.p>
          </div>
          
          {/* Security Status Indicator */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.5 }}
            className="bg-black/40 backdrop-blur-sm rounded-lg border border-red-900/30 p-3 mb-4 sm:mb-6"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center">
                <img src="https://pub-0f77feecf89849e2bd80779e26ad94bb.r2.dev/shieldxgif.gif" alt="ShieldX" className="h-4 w-4 mr-2" />
                <span className="text-xs sm:text-sm font-medium">System Security Status</span>
              </div>
              <div className="flex items-center">
                <span className="h-2 w-2 bg-green-500 rounded-full animate-pulse mr-1"></span>
                <span className="text-xs text-green-400">OPERATIONAL</span>
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-2 text-xs">
              <div className="bg-black/30 rounded p-2 border border-slate-800/60">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-slate-400 flex items-center">
                    <Lock className="h-3 w-3 mr-1 text-red-500" />
                    Security
                  </span>
                  <span className="text-green-400">{systemStatus.securityModules}%</span>
                </div>
                <div className="h-1 bg-slate-800 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-red-800 to-red-600 rounded-full" 
                    style={{ width: `${systemStatus.securityModules}%` }}
                  ></div>
                </div>
              </div>
              
              <div className="bg-black/30 rounded p-2 border border-slate-800/60">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-slate-400 flex items-center">
                    <Server className="h-3 w-3 mr-1 text-red-500" />
                    Database
                  </span>
                  <span className="text-green-400">{systemStatus.databaseConnection}%</span>
                </div>
                <div className="h-1 bg-slate-800 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-red-800 to-red-600 rounded-full" 
                    style={{ width: `${systemStatus.databaseConnection}%` }}
                  ></div>
                </div>
              </div>
              
              <div className="bg-black/30 rounded p-2 border border-slate-800/60">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-slate-400 flex items-center">
                    <Activity className="h-3 w-3 mr-1 text-red-500" />
                    API
                  </span>
                  <span className="text-green-400">{systemStatus.apiServers}%</span>
                </div>
                <div className="h-1 bg-slate-800 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-red-800 to-red-600 rounded-full" 
                    style={{ width: `${systemStatus.apiServers}%` }}
                  ></div>
                </div>
              </div>
            </div>
            
            <div className="mt-2 text-center text-xs text-slate-500">
              <span>Last security scan: {systemStatus.lastScan}</span>
            </div>
          </motion.div>
          
          {/* Login Form */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.5 }}
            className="bg-black/60 backdrop-blur-md rounded-lg border border-slate-800 overflow-hidden"
          >
            <div className="p-4 sm:p-6">
              <form onSubmit={handleLogin} className="space-y-4 sm:space-y-5">
                {/* System Access Alert */}
                <div className="bg-red-900/10 border border-red-900/30 rounded-md p-2 sm:p-3 text-xs sm:text-sm mb-3 sm:mb-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <AlertTriangle className="h-4 w-4 sm:h-5 sm:w-5 text-red-500" />
                    </div>
                    <div className="ml-3">
                      <h3 className="text-xs sm:text-sm font-medium text-red-500">Authorized Access Only</h3>
                      <div className="mt-1 text-xs text-slate-400">
                        <p>This system is restricted to authorized security personnel only. All access attempts are logged and monitored.</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Email Field */}
                <div>
                  <label htmlFor="email" className="block text-xs sm:text-sm font-medium text-slate-400 mb-1">
                    Email Address
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <User className="h-4 w-4 sm:h-5 sm:w-5 text-slate-500" />
                    </div>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className={`block w-full pl-10 pr-3 py-2 sm:py-2.5 bg-black/50 border ${validationErrors.email ? 'border-red-600' : 'border-slate-700'} rounded-md shadow-sm placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-red-600 focus:border-red-600 sm:text-sm`}
                      placeholder="authorized@example.com"
                    />
                  </div>
                  {validationErrors.email && (
                    <p className="mt-1 text-xs text-red-500">{validationErrors.email}</p>
                  )}
                </div>
                
                {/* Password Field */}
                <div>
                  <label htmlFor="password" className="block text-xs sm:text-sm font-medium text-slate-400 mb-1">
                    Password
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Lock className="h-4 w-4 sm:h-5 sm:w-5 text-slate-500" />
                    </div>
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      autoComplete="current-password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className={`block w-full pl-10 pr-10 py-2 sm:py-2.5 bg-black/50 border ${validationErrors.password ? 'border-red-600' : 'border-slate-700'} rounded-md shadow-sm placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-red-600 focus:border-red-600 sm:text-sm`}
                      placeholder="••••••••••••••••"
                    />
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="text-slate-500 hover:text-white focus:outline-none"
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4 sm:h-5 sm:w-5" />
                        ) : (
                          <Eye className="h-4 w-4 sm:h-5 sm:w-5" />
                        )}
                      </button>
                    </div>
                  </div>
                  {validationErrors.password && (
                    <p className="mt-1 text-xs text-red-500">{validationErrors.password}</p>
                  )}
                </div>
                
                {/* Login Error Message */}
                {(loginStatus === 'error' || authError) && (
                  <div className="bg-red-900/20 border border-red-900/40 rounded-md p-3 flex items-start">
                    <AlertTriangle className="h-4 w-4 sm:h-5 sm:w-5 text-red-500 mr-2 mt-0.5" />
                    <div>
                      <p className="text-xs sm:text-sm text-red-500 font-medium">Authentication Failed</p>
                      <p className="text-xs text-slate-400">
                        {authError || "Invalid email or password. Please try again."}
                        {loginAttempts >= 3 && " For assistance, contact system administrators."}
                      </p>
                    </div>
                  </div>
                )}
                
                {/* Login Button */}
                <div>
                  <button
                    type="submit"
                    disabled={loginStatus === 'loading'}
                    className={`w-full flex items-center justify-center px-4 py-2 sm:py-2.5 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
                      loginStatus === 'loading' 
                        ? 'bg-slate-700 cursor-not-allowed' 
                        : 'bg-red-800 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-600'
                    } transition-colors duration-200`}
                  >
                    {loginStatus === 'loading' ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Authenticating...
                      </>
                    ) : (
                      <>
                        <span>Secure Login</span>
                        <ChevronRight className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
            
            {/* Terminal-like Footer */}
            <div className="flex items-center justify-between bg-black/80 px-3 sm:px-4 py-2 border-t border-slate-800/60 text-xs text-slate-500 font-mono">
              <div className="flex items-center">
                <Cpu className="h-3 w-3 mr-1 text-red-600" />
                <span>AlertStrike AI SYSTEM</span>
              </div>
              <div className="flex items-center">
                <div className="h-1.5 w-1.5 bg-green-600 rounded-full mr-1"></div>
                <span>256-BIT ENCRYPTION</span>
              </div>
            </div>
          </motion.div>
          
          {/* Footer */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6, duration: 0.5 }}
            className="mt-4 sm:mt-6 text-center text-xs text-slate-500"
          >
            <p>© 2025 AlertStrike. Advanced AI SIEM & Threat Detection and Response.</p>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Login;
